<template>
  <div class="max-w-4xl mx-auto space-y-6 animate-fade-in pb-10">

    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Reglas de Inventario</h1>
        <p class="text-slate-500 dark:text-slate-400 text-sm mt-1">Define las políticas de valoración, rotación y control de stock.</p>
      </div>
      <button
        @click="saveSettings"
        :disabled="isSaving || isLoading || !form.id"
        class="flex items-center gap-2 px-6 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl shadow-lg shadow-brand-500/30 transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
      >
        <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
        <Save v-else class="w-4 h-4" />
        Guardar Cambios
      </button>
    </div>

    <div v-if="isLoading" class="py-20 flex justify-center">
      <Loader2 class="w-10 h-10 text-brand-500 animate-spin" />
    </div>

    <div v-else-if="!form.id" class="p-10 text-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
      <p class="text-slate-500 mb-4">No se ha inicializado la configuración de inventario para esta empresa.</p>
      <button @click="initializeSettings" class="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium transition-colors">
        Inicializar con valores por defecto
      </button>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">

      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6">
        <h3 class="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <Calculator class="w-4 h-4 text-brand-600" /> Valoración y Costos
        </h3>

        <div class="space-y-5">
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Método de Valuación</label>
            <select v-model="form.costing_method" class="form-input-premium">
              <option value="AVERAGE">Promedio Ponderado (Estándar)</option>
              <option value="FIFO">PEPS (Primeras Entradas, Primeras Salidas)</option>
            </select>
            <p class="text-xs text-slate-400 mt-1">Define el cálculo del costo de los productos vendidos (COGS).</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Cierre Contable (Lock Date)</label>
            <input type="date" v-model="form.inventory_lock_date" class="form-input-premium" />
            <p class="text-xs text-slate-400 mt-1">Bloquea movimientos de inventario anteriores a esta fecha.</p>
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6">
        <h3 class="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <ClipboardCheck class="w-4 h-4 text-brand-600" /> Calidad y Rotación
        </h3>

        <div class="space-y-4">
          <label class="flex items-start gap-3 p-3 rounded-xl border border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors">
            <div class="relative mt-1">
              <input type="checkbox" v-model="form.enforce_fefo" class="sr-only peer">
              <div class="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
            </div>
            <div>
              <span class="block text-sm font-medium text-slate-700 dark:text-slate-200">Priorizar Vencimiento (FEFO)</span>
              <span class="text-xs text-slate-500 block mt-0.5">Sugiere automáticamente el lote más próximo a vencer o caducar.</span>
            </div>
          </label>

          <label class="flex items-start gap-3 p-3 rounded-xl border border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors">
            <div class="relative mt-1">
              <input type="checkbox" v-model="form.enforce_batches" class="sr-only peer">
              <div class="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
            </div>
            <div>
              <span class="block text-sm font-medium text-slate-700 dark:text-slate-200">Trazabilidad por Lotes</span>
              <span class="text-xs text-slate-500 block mt-0.5">Exige registrar lote y fecha de caducidad al recibir mercadería.</span>
            </div>
          </label>

          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Alerta de Caducidad</label>
            <div class="relative">
              <span class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">Días</span>
              <input type="number" v-model="form.default_expiration_alert_days" class="form-input-premium pl-12" placeholder="90" />
            </div>
          </div>
        </div>
      </div>

      <div class="md:col-span-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6">
        <h3 class="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <Truck class="w-4 h-4 text-brand-600" /> Logística Operativa
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <label class="flex items-start gap-3 p-3 rounded-xl border border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors">
            <div class="relative mt-1">
              <input type="checkbox" v-model="form.strict_transfer_approval" class="sr-only peer">
              <div class="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
            </div>
            <div>
              <span class="block text-sm font-medium text-slate-700 dark:text-slate-200">Recepción Estricta</span>
              <span class="text-xs text-slate-500 block mt-0.5">La sucursal destino debe contar y aceptar explícitamente la mercadería recibida.</span>
            </div>
          </label>

          <label class="flex items-start gap-3 p-3 rounded-xl border border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors border-l-4 border-l-red-500">
            <div class="relative mt-1">
              <input type="checkbox" v-model="form.allow_negative_stock" class="sr-only peer">
              <div class="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-500"></div>
            </div>
            <div>
              <span class="block text-sm font-medium text-slate-700 dark:text-slate-200">Stock Negativo</span>
              <span class="text-xs text-slate-500 block mt-0.5">Permitir facturar productos aunque el sistema indique saldo cero.</span>
            </div>
          </label>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { Save, Loader2, Calculator, ClipboardCheck, Truck } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

const { showSuccess, showError } = useToast();
const isLoading = ref(true);
const isSaving = ref(false);

const currentUser = SecureStorage.getItem('llevo_user');
const currentCompanyId = currentUser?.company;

const form = reactive({
  id: null,
  company: currentCompanyId,
  costing_method: 'AVERAGE',
  allow_negative_stock: false,
  strict_transfer_approval: true,
  enforce_batches: true,
  enforce_fefo: true,
  default_expiration_alert_days: 90,
  inventory_lock_date: null
});

onMounted(async () => {
  if (!currentCompanyId) return;
  isLoading.value = true;
  try {
    const response = await apiClient.get(`/inventory_settings?company=${currentCompanyId}`);
    // Si response.data es array, tomamos el primero. Si es objeto, lo usamos directo.
    const data = Array.isArray(response.data) ? response.data[0] : response.data;

    if (data && data.id) {
      Object.assign(form, {
        id: data.id,
        company: data.company,
        costing_method: data.costing_method,
        allow_negative_stock: Boolean(data.allow_negative_stock),
        strict_transfer_approval: Boolean(data.strict_transfer_approval),
        enforce_batches: Boolean(data.enforce_batches),
        enforce_fefo: Boolean(data.enforce_fefo),
        default_expiration_alert_days: data.default_expiration_alert_days,
        inventory_lock_date: data.inventory_lock_date
      });
    }
  } catch (error) {
    console.error("Error cargando configuración:", error);
  } finally {
    isLoading.value = false;
  }
});

// Función para crear la configuración inicial si no existe
const initializeSettings = async () => {
  isSaving.value = true;
  try {
    const payload = { ...form };
    // Aseguramos que company_id esté presente
    payload.company = currentCompanyId;
    delete payload.id; // Es nuevo

    const res = await apiClient.post('/inventory_settings', payload);
    if (res.data && res.data.id) {
      form.id = res.data.id;
      showSuccess('Configuración inicializada correctamente.');
    }
  } catch (error) {
    showError('Error al inicializar.');
  } finally {
    isSaving.value = false;
  }
};

const saveSettings = async () => {
  if (!form.id) return; // Solo permite editar

  isSaving.value = true;
  try {
    await apiClient.put(`/inventory_settings/${form.id}`, form);
    showSuccess('Reglas de inventario actualizadas.');
  } catch (error) {
    console.error(error);
    showError('No se pudieron guardar las reglas.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in 0.3s ease-out forwards;
}
</style>